import {Component, EventEmitter, Input, Output} from '@angular/core';


export interface UiSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

@Component({
  selector: 'ui-select',
  standalone: true,
  imports: [],
  templateUrl: './ui-select.component.html',
  styleUrl: './ui-select.component.scss'
})
export class UiSelectComponent {
  private static nextId = 0;

  @Input() options: UiSelectOption[] = [];

  /**
   * Selected value.
   * Note: options are keyed by string values; we normalize whatever we get to a string.
   */
  @Input()
  get value(): string {
    return this._value;
  }
  set value(v: any) {
    // Ensure stable string comparison even if parent provides numbers/enums.
    this._value = v === null || v === undefined ? '' : String(v);
  }
  private _value: string = '';

  @Input() placeholder = 'Select an option';
  @Input() disabled = false;
  @Input() size: 'sm' | 'md' | 'lg' = 'md';
  @Input() fullWidth = false;
  @Input() ariaLabel?: string;

  /**
   * Display mode: 'dropdown' (traditional select) or 'button-group' (radio buttons styled as buttons)
   */
  @Input() mode: 'dropdown' | 'button-group' = 'dropdown';

  /**
   * Layout direction for button-group mode
   */
  @Input() direction: 'horizontal' | 'vertical' = 'horizontal';

  /**
   * Variant for button-group mode
   */
  @Input() variant: 'primary' | 'secondary' | 'success' | 'warning' | 'error' = 'primary';

  @Output() valueChange = new EventEmitter<string>();

  groupName: string;

  constructor() {
    this.groupName = 'ui-select-group-' + UiSelectComponent.nextId++;
  }

  onChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.value = target.value;
    this.valueChange.emit(this.value);
  }

  onRadioChange(value: string): void {
    this.value = value;
    this.valueChange.emit(this.value);
  }

  get computedClasses(): string {
    const classes = ['select', 'select-bordered'];

    if (this.size === 'sm') {
      classes.push('select-sm');
    } else if (this.size === 'lg') {
      classes.push('select-lg');
    }

    if (this.fullWidth) {
      classes.push('w-full');
    }

    return classes.join(' ');
  }

  get buttonGroupContainerClasses(): string {
    const classes = [];

    if (this.direction === 'horizontal') {
      classes.push('flex', 'flex-wrap', 'gap-2');
      if (this.fullWidth) {
        classes.push('w-full');
      }
    } else {
      classes.push('flex', 'flex-col', 'gap-2');
    }

    return classes.join(' ');
  }

  get buttonClasses(): string {
    const classes = ['btn'];

    // Variant
    const variantMap = {
      primary: 'btn-primary',
      secondary: 'btn-secondary',
      success: 'btn-success',
      warning: 'btn-warning',
      error: 'btn-error',
    };
    classes.push(variantMap[this.variant]);

    // Size
    if (this.size === 'sm') {
      classes.push('btn-sm');
    } else if (this.size === 'lg') {
      classes.push('btn-lg');
    }

    return classes.join(' ');
  }
}
